import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp from 'components/StyledComp';
import DataTables from 'components-desktop/DataTables';
import TfootSummary from 'components-desktop/TfootSummary';
import helpers from 'misc/helpers';

const propTypes = {
  title: PropTypes.string,
  timeFormat: PropTypes.string,
  payrollReport: PropTypes.object.isRequired,
};

const defaultProps = {
  title: '',
  timeFormat: 'Standard',
};

@autobind
class TablePayrolls extends BaseComponent {
  constructor(props) {
    super(props);
  }

  createColumns() {
    return [{
      title: 'Id',
      dataIndex: 'caregiverId',
      sorter: (a, b) => a.caregiverId - b.caregiverId,
    }, {
      title: 'Caregiver',
      dataIndex: 'caregiverName',
      sorter: (a, b) => a.caregiverName?.localeCompare(b.caregiverName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/caregivers/caregiver_profile?id=${record.caregiverId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
      defaultSortOrder: 'ascend',
    }, {
      title: 'SSN',
      dataIndex: 'caregiverSsn',
      sorter: (a, b) => a.caregiverSsn?.localeCompare(b.caregiverSsn),
    }, {
      title: 'Coordinator',
      dataIndex: 'coordinator',
      sorter: (a, b) => a.coordinator?.localeCompare(b.coordinator),
    }, {
      title: 'Team',
      dataIndex: 'team',
      sorter: (a, b) => a.team?.localeCompare(b.team),
    }, {
      title: 'Company',
      dataIndex: 'company',
      sorter: (a, b) => a.company?.localeCompare(b.company),
    }, {
      title: 'Pickup Team',
      dataIndex: 'pickupTeam',
      sorter: (a, b) => a.pickupTeam?.localeCompare(b.pickupTeam),
    }, {
      title: 'Pay Method',
      dataIndex: 'paymentMethod',
      sorter: (a, b) => a.paymentMethod?.localeCompare(b.paymentMethod),
    }, {
      title: 'Pay Rate',
      dataIndex: 'payRate1InDollars',
      sorter: (a, b) => a.payRate1 - b.payRate1,
      render: (text, record) => (
        <styledComp.Span
          width="wrap_content"
          height="wrap_content"
          color="textPrimaryDark"
          fontSize="12px"
        >
          {(!record.payRate2InDollars)
            ? record.payRate1InDollars
            : `${record.payRate1InDollars} - ${record.payRate2InDollars}`
          }
        </styledComp.Span>
      ),
    }, {
      title: 'Reg. Hours',
      dataIndex: 'regularHours',
      sorter: (a, b) => a.regularMinutes - b.regularMinutes,
    }, {
      title: 'Visit',
      dataIndex: 'regularVisits',
      sorter: (a, b) => a.regularVisits - b.regularVisits,
    }, {
      title: 'Daily',
      dataIndex: 'regularDailies',
      sorter: (a, b) => a.regularDailies - b.regularDailies,
    }, {
      title: 'Reg. Amt.',
      dataIndex: 'regularAmountInDollars',
      sorter: (a, b) => a.regularAmount - b.regularAmount,
    }, {
      title: 'OT Hours',
      dataIndex: 'overtimeHours',
      sorter: (a, b) => a.overtimeMinutes - b.overtimeMinutes,
    }, {
      title: 'OT Amt.',
      dataIndex: 'overtimeAmountInDollars',
      sorter: (a, b) => a.overtimeAmount - b.overtimeAmount,
    }, {
      title: 'Total Hours',
      dataIndex: 'totalHours',
      sorter: (a, b) => a.totalMinutes - b.totalMinutes,
    }, {
      title: 'Total Amt.',
      dataIndex: 'totalAmountInDollars',
      sorter: (a, b) => a.totalAmount - b.totalAmount,
    }];
  }

  createDataSource(payrollReport) {
    const rows = payrollReport.getRemoteObjects('rows');

    const dataSource = [];
    rows.forEach((row, index) => {
      dataSource.push({
        key: row.getLong('caregiverId'),
        caregiverId: row.getLong('caregiverId'),
        caregiverName: row.getString('caregiverName'),
        caregiverSsn: row.getString('caregiverSsn'),
        coordinator: row.getString('coordinator'),
        team: row.getString('team'),
        company: row.getString('company'),
        pickupTeam: row.getString('pickupTeam'),
        paymentMethod: row.getString('paymentMethod'),
        payRate1: row.getDouble('payRate1'),
        payRate2: row.getDouble('payRate2'),
        payRate1InDollars: helpers.toDollars(row.getDouble('payRate1')),
        payRate2InDollars: helpers.toDollars(row.getDouble('payRate2')),
        regularMinutes: row.getRemoteObject('payrollData')?.getLong('regularMinutes'),
        regularHours: this.formatMinutesToHoursString(
          row.getRemoteObject('payrollData')?.getLong('regularMinutes')),
        regularVisits: row.getRemoteObject('payrollData')?.getLong('regularVisits'),
        regularDailies: row.getRemoteObject('payrollData')?.getLong('regularDailies'),
        regularAmount: row.getRemoteObject('payrollData')?.getDouble('regularAmount'),
        regularAmountInDollars:
            helpers.toDollars(row.getRemoteObject('payrollData')?.getDouble('regularAmount')),
        overtimeMinutes: row.getRemoteObject('payrollData')?.getLong('overtimeMinutes'),
        overtimeHours: this.formatMinutesToHoursString(
          row.getRemoteObject('payrollData')?.getLong('overtimeMinutes')),
        overtimeAmount: row.getRemoteObject('payrollData')?.getDouble('overtimeAmount'),
        overtimeAmountInDollars:
            helpers.toDollars(row.getRemoteObject('payrollData')?.getDouble('overtimeAmount')),
        totalMinutes: row.getRemoteObject('payrollData')?.getLong('totalMinutes'),
        totalHours: this.formatMinutesToHoursString(
          row.getRemoteObject('payrollData')?.getLong('totalMinutes')),
        totalAmount: row.getRemoteObject('payrollData')?.getDouble('totalAmount'),
        totalAmountInDollars:
            helpers.toDollars(row.getRemoteObject('payrollData')?.getDouble('totalAmount')),
      });
    });
    return dataSource;
  }

  renderTfoot(columnsToDisplay, dataRowsToDisplay) {
    return (
      <TfootSummary
        columnsToDisplay={columnsToDisplay}
        dataRowsToDisplay={dataRowsToDisplay}
        isTableSelectable={false}
        columnKeysToAlign={['regularHours', 'regularVisits', 'regularDailies',
          'regularAmountInDollars', 'overtimeHours', 'overtimeAmountInDollars', 'totalHours',
          'totalAmountInDollars']}
        reducers={[
          TfootSummary.SUM_REDUCER_GENERATOR('regularMinutes'),
          TfootSummary.SUM_REDUCER_GENERATOR('regularVisits'),
          TfootSummary.SUM_REDUCER_GENERATOR('regularDailies'),
          TfootSummary.SUM_DOLLAR_REDUCER_GENERATOR('regularAmount'),
          TfootSummary.SUM_REDUCER_GENERATOR('overtimeMinutes'),
          TfootSummary.SUM_DOLLAR_REDUCER_GENERATOR('overtimeAmount'),
          TfootSummary.SUM_REDUCER_GENERATOR('totalMinutes'),
          TfootSummary.SUM_DOLLAR_REDUCER_GENERATOR('totalAmount'),
        ]}
        formatters={[
          n => this.formatMinutesToHoursString(n),
          n => n,
          n => n,
          n => helpers.toDollars(n),
          n => this.formatMinutesToHoursString(n),
          n => helpers.toDollars(n),
          n => this.formatMinutesToHoursString(n),
          n => helpers.toDollars(n),
        ]}
      />
    );
  }

  formatMinutesToHoursString(minutes) {
    return this.props.timeFormat === 'Standard'
      ? helpers.min2ClockHour(minutes)
      : helpers.min2DecimalHour(minutes);
  }

  renderContent() {
    const { payrollReport, title, ...layoutProps } = this.props;

    return (
      <DataTables
        {...layoutProps}
        ref={(ref) => { this.dataTables = ref; }}
        color="blue_hoki"
        fontSize="12px"
        size="small"
        title={title}
        icon="file-pdf"
        columns={this.createColumns()}
        dataSource={this.createDataSource(payrollReport)}
        tfoot={this.renderTfoot}
      />
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TablePayrolls.propTypes = propTypes;
TablePayrolls.defaultProps = defaultProps;

export default TablePayrolls;
